import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAPIBaseURL } from '../services/helpers'
import { getData, postData } from '../services/request'
import LatLngMapPicker from '../components/LatLngMapPicker'
import SkillsTagInput from '../components/SkillsTagInput'
import { COUNTRIES } from '../data/countries'
import {
  formToProfilePayload,
  getLoggedInUserEmail,
  hasUploadedCv,
  inputClass,
  labelClass,
  profileToFormState
} from './employmeProfileUtils'
import CvFileUpload from './CvFileUpload'
import { appendReferralAttribution } from '../services/referralAttribution'

const STEP_LABELS = ['Your CV', 'Your details', 'Review']

const CvEmptyValue = ({ label }) => (
  <div className="flex items-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-4 py-3">
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
      </svg>
    </span>
    <span className="text-sm italic text-slate-400">{label || 'Not provided'}</span>
  </div>
)

const CvSection = ({ title, icon, children, delay = 0 }) => (
  <section
    className="animate-slide-up opacity-0"
    style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
  >
    <div className="mb-2 flex items-center gap-2">
      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-50 text-primary-600">{icon}</span>
      <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{title}</h3>
    </div>
    {children}
  </section>
)

const getNameAvatarInitial = (fullName) => {
  const firstWord = (fullName || '').trim().split(/\s+/).filter(Boolean)[0]
  if (!firstWord) return ''
  return firstWord.charAt(0).toUpperCase()
}

const ProfileAvatar = ({ fullName, size = 'lg' }) => {
  const initial = getNameAvatarInitial(fullName)
  const sizeClass =
    size === 'lg'
      ? 'h-16 w-16 text-2xl ring-4 ring-white/10'
      : 'h-10 w-10 text-sm ring-2 ring-white/10'

  if (!initial) {
    return (
      <div
        className={`${sizeClass} flex shrink-0 items-center justify-center rounded-full border border-dashed border-white/30 bg-white/5 text-white/35`}
        aria-hidden="true"
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </div>
    )
  }

  return (
    <div
      className={`${sizeClass} flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-primary-600 font-bold text-white shadow-lg shadow-primary-900/30`}
      aria-hidden="true"
    >
      {initial}
    </div>
  )
}

const CvPreview = ({ form }) => {
  const hasSkills = form.skills.length > 0
  const yearsLabel =
    form.yearsOfExperience !== ''
      ? `${form.yearsOfExperience} year${Number(form.yearsOfExperience) === 1 ? '' : 's'} of experience`
      : ''

  return (
    <div className="animate-scale-in overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/60">
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-7 text-white">
        <div
          className="flex items-center gap-4 animate-slide-up opacity-0"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards' }}
        >
          <ProfileAvatar fullName={form.fullName} />
          <div className="min-w-0 flex-1">
            {form.fullName ? (
              <h2 className="text-2xl font-bold tracking-tight">{form.fullName}</h2>
            ) : (
              <div className="inline-flex items-center gap-2 rounded-lg border border-dashed border-white/25 bg-white/5 px-4 py-2">
                <span className="text-sm italic text-white/40">Full name not provided</span>
              </div>
            )}
            {form.specialization ? (
              <p className="mt-1.5 text-sm font-medium text-primary-100">{form.specialization}</p>
            ) : (
              <p className="mt-1.5 text-sm italic text-white/35">Specialization not provided</p>
            )}
          </div>
        </div>
        <div
          className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-300 animate-slide-up opacity-0"
          style={{ animationDelay: '140ms', animationFillMode: 'forwards' }}
        >
          {form.contactEmail ? (
            <span className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {form.contactEmail}
            </span>
          ) : (
            <span className="italic text-white/30">Email not provided</span>
          )}
          {form.contactPhone ? (
            <span className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              {form.contactPhone}
            </span>
          ) : (
            <span className="italic text-white/30">Phone not provided</span>
          )}
          {form.locationCountry ? (
            <span className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {form.locationCountry}
            </span>
          ) : (
            <span className="italic text-white/30">Country not provided</span>
          )}
        </div>
      </div>

      <div className="space-y-6 px-6 py-6">
        <CvSection
          title="Skills"
          delay={200}
          icon={
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          }
        >
          {hasSkills ? (
            <div className="flex flex-wrap gap-2">
              {form.skills.map((skill) => (
                <span
                  key={skill}
                  className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                >
                  {skill}
                </span>
              ))}
            </div>
          ) : (
            <CvEmptyValue label="No skills added yet" />
          )}
        </CvSection>

        <CvSection
          title="Experience"
          delay={280}
          icon={
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
        >
          {yearsLabel ? (
            <p className="mb-2 text-sm font-semibold text-slate-800">{yearsLabel}</p>
          ) : (
            <div className="mb-2">
              <CvEmptyValue label="Years of experience not provided" />
            </div>
          )}
          {form.experienceSummary ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{form.experienceSummary}</p>
          ) : (
            <CvEmptyValue label="Experience details not provided" />
          )}
        </CvSection>

        <CvSection
          title="Education"
          delay={360}
          icon={
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 14l9-5-9-5-9 5 9 5z" />
              <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
            </svg>
          }
        >
          {form.educationSummary ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{form.educationSummary}</p>
          ) : (
            <CvEmptyValue label="Education details not provided" />
          )}
        </CvSection>

        {hasUploadedCv(form.cvUrl) ? (
          <CvSection
            title="CV document"
            delay={440}
            icon={
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          >
            {form.cvViewUrl ? (
            <a
              href={form.cvViewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
            >
              View attached CV
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            ) : (
              <p className="text-sm text-amber-700">Please re-upload your CV to view it securely.</p>
            )}
          </CvSection>
        ) : null}
      </div>
    </div>
  )
}

const JobSeekerOnboarding = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [hasExistingProfile, setHasExistingProfile] = useState(false)
  const [animKey, setAnimKey] = useState(0)

  const [form, setForm] = useState({
    cvUrl: '',
    cvViewUrl: '',
    fullName: '',
    contactEmail: getLoggedInUserEmail(),
    contactPhone: '',
    locationCountry: '',
    locationLat: '',
    locationLng: '',
    specialization: '',
    skills: [],
    educationSummary: '',
    yearsOfExperience: '',
    experienceSummary: ''
  })

  useEffect(() => {
    const isLoggedIn =
      typeof window !== 'undefined' &&
      (sessionStorage.getItem('employme-active') === '1' || !!localStorage.getItem('employmeUser'))
    if (!isLoggedIn) {
      navigate('/login', { replace: true })
      return
    }

    const fetchProfile = async () => {
      setLoading(true)
      setError('')
      const url = `${getAPIBaseURL()}/employme/jobseeker/profile`
      const { error: reqError, result } = await getData(url, {}, {})
      setLoading(false)
      if (reqError || !result) {
        return
      }
      if (result.status === 200) {
        const profile = result.data?.data || {}
        setHasExistingProfile(true)
        setForm((prev) => ({ ...prev, ...profileToFormState(profile) }))
      }
    }

    fetchProfile()
  }, [navigate])

  const onChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const goToStep = (nextStep) => {
    setAnimKey((k) => k + 1)
    setStep(nextStep)
  }

  const saveProfile = async (nextAction) => {
    setSaving(true)
    setError('')
    setInfo('')
    const url = `${getAPIBaseURL()}/employme/jobseeker/profile`

    const payload = formToProfilePayload(form)

    if (nextAction === 'dashboard' || nextAction === 'profile') {
      payload.onboardingComplete = true
    }

    const requiresLatLng =
      (step === 2 && nextAction === 'nextStep') || (step === 3 && (nextAction === 'dashboard' || nextAction === 'profile'))

    if (requiresLatLng) {
      const lat = Number(form.locationLat)
      const lng = Number(form.locationLng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        setSaving(false)
        setError('Please select your exact location on the map before continuing.')
        return
      }
    }

    const { error: reqError, result } = await postData(url, {}, appendReferralAttribution(payload))
    setSaving(false)
    if (reqError || !result) {
      setError(result?.data?.data || 'Unable to save your profile right now.')
      return
    }
    if (result.status === 200 || result.status === 201) {
      setInfo('Profile updated.')
      setHasExistingProfile(true)
      if (nextAction === 'dashboard') {
        navigate('/jobs', { replace: true })
      } else if (nextAction === 'profile') {
        navigate('/jobs/profile', { replace: true })
      } else if (nextAction === 'nextStep') {
        goToStep(Math.min(step + 1, 3))
      }
    } else {
      setError(result.data?.data || 'Unable to save your profile right now.')
    }
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10 animate-fade-in">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="mb-6 flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex flex-1 items-center">
                <div className="h-9 w-9 animate-pulse-soft rounded-full bg-slate-200" />
                {s < 3 && <div className="mx-2 h-px flex-1 bg-slate-200" />}
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <div className="h-6 w-48 animate-pulse-soft rounded-lg bg-slate-200" />
            <div className="h-4 w-full animate-pulse-soft rounded bg-slate-100" />
            <div className="h-4 w-2/3 animate-pulse-soft rounded bg-slate-100" />
            <div className="mt-6 h-11 w-full animate-pulse-soft rounded-xl bg-slate-100" />
          </div>
          <p className="mt-6 text-center text-sm text-slate-500">Loading your onboarding...</p>
        </div>
      </div>
    )
  }

  const progressPercent = ((step - 1) / (STEP_LABELS.length - 1)) * 100

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="mb-3 flex items-center justify-between text-xs font-medium">
        {STEP_LABELS.map((label, i) => {
          const stepNum = i + 1
          const isActive = step === stepNum
          const isDone = step > stepNum
          return (
            <span
              key={label}
              className={`transition-colors duration-300 ${
                isActive ? 'text-primary-600' : isDone ? 'text-emerald-600' : 'text-slate-400'
              }`}
            >
              {label}
            </span>
          )
        })}
      </div>
      <div className="relative h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-500 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className="mt-4 flex items-center justify-between">
        {STEP_LABELS.map((label, i) => {
          const stepNum = i + 1
          const isActive = step === stepNum
          const isDone = step > stepNum
          return (
            <div key={label} className="flex flex-1 items-center">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                  isActive
                    ? 'scale-110 bg-primary-600 text-white shadow-md shadow-primary-500/30'
                    : isDone
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {isDone ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  stepNum
                )}
              </div>
              {stepNum < 3 && (
                <div className={`mx-2 h-px flex-1 transition-colors duration-300 ${isDone ? 'bg-emerald-200' : 'bg-slate-200'}`} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div key={`step-${animKey}`} className="animate-slide-up">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-2">Welcome to Employ Me</h1>
          <p className="text-sm leading-relaxed text-slate-500 mb-6">
            Let&apos;s set up your profile so employers can quickly understand your experience.
            You can update this later from your profile page.
          </p>
          <div className="space-y-4">
            <CvFileUpload
              cvUrl={form.cvUrl}
              cvViewUrl={form.cvViewUrl}
              disabled={saving}
              onCvUploaded={(url, viewUrl) =>
                setForm((prev) => ({ ...prev, cvUrl: url, cvViewUrl: viewUrl || '' }))
              }
            />
          </div>
        </div>
      )
    }

    if (step === 2) {
      return (
        <div key={`step-${animKey}`} className="animate-slide-up">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-2">Tell us about you</h1>
          <p className="text-sm leading-relaxed text-slate-500 mb-6">
            We&apos;ll use this information to help match you with the right roles.
          </p>
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={labelClass}>Full name</label>
                <input name="fullName" value={form.fullName} onChange={onChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Phone number</label>
                <input name="contactPhone" value={form.contactPhone} onChange={onChange} className={inputClass} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                name="contactEmail"
                value={form.contactEmail}
                readOnly
                className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-500"
              />
            </div>
            <div>
              <label className={labelClass}>Country</label>
              <select
                name="locationCountry"
                value={form.locationCountry}
                onChange={onChange}
                className={inputClass}
              >
                <option value="">Select country</option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Select your location on the map (required)</label>
              <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                <LatLngMapPicker
                  value={
                    form.locationLat && form.locationLng
                      ? { lat: Number(form.locationLat), lng: Number(form.locationLng) }
                      : null
                  }
                  disabled={saving}
                  onChange={({ lat, lng }) => {
                    setForm((prev) => ({
                      ...prev,
                      locationLat: String(lat),
                      locationLng: String(lng)
                    }))
                  }}
                />
              </div>
              {!(form.locationLat && form.locationLng) ? (
                <p className="mt-1.5 text-[11px] font-medium text-red-500">
                  You must select a precise map location to continue.
                </p>
              ) : null}
            </div>
            <div>
              <label className={labelClass}>Specialization</label>
              <input
                name="specialization"
                value={form.specialization}
                onChange={onChange}
                placeholder="e.g. Software engineering, Nursing, Accounting"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Skills</label>
              <SkillsTagInput
                skills={form.skills}
                disabled={saving}
                onChange={(skills) => setForm((prev) => ({ ...prev, skills }))}
              />
              <p className="mt-1.5 text-[11px] text-slate-400">Press Enter after each skill to add it.</p>
            </div>
            <div>
              <label className={labelClass}>Education</label>
              <textarea
                name="educationSummary"
                value={form.educationSummary}
                onChange={onChange}
                rows={4}
                placeholder="Tell us about your education — schools, degrees, certifications, and relevant training..."
                className={`${inputClass} resize-y`}
              />
            </div>
            <div>
              <label className={labelClass}>Years of experience</label>
              <input
                name="yearsOfExperience"
                value={form.yearsOfExperience}
                onChange={onChange}
                type="number"
                min="0"
                placeholder="e.g. 3"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Experience</label>
              <textarea
                name="experienceSummary"
                value={form.experienceSummary}
                onChange={onChange}
                rows={5}
                placeholder="Describe your work experience — roles, responsibilities, achievements, and industries you've worked in..."
                className={`${inputClass} resize-y`}
              />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div key={`step-${animKey}`} className="animate-slide-up">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-2">Review your profile</h1>
        <p className="text-sm leading-relaxed text-slate-500 mb-6">
          Here&apos;s how employers will see your profile. Empty sections are marked so you know what to complete later.
        </p>
        <CvPreview form={form} />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 animate-fade-in">
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xl shadow-slate-200/50">
        <div className="h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-sky-500" />
        <div className="p-6 sm:p-8">
          {renderStepIndicator()}
          {error && (
            <div className="mb-5 animate-slide-up rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}
          {!error && info && (
            <div className="mb-5 animate-slide-up rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {info}
            </div>
          )}
          {renderStepContent()}
          <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
            <button
              type="button"
              disabled={step === 1 || saving}
              onClick={() => goToStep(Math.max(step - 1, 1))}
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:opacity-40"
            >
              Back
            </button>
            <div className="flex gap-2">
              {step < 3 && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => saveProfile('nextStep')}
                  className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary-500/25 transition-all hover:bg-primary-700 hover:shadow-lg disabled:opacity-60"
                >
                  {saving ? 'Saving...' : 'Save & continue'}
                </button>
              )}
              {step === 3 && (
                <>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => saveProfile('profile')}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-all hover:bg-slate-50 disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : 'Go to full profile'}
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => saveProfile('dashboard')}
                    className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary-500/25 transition-all hover:bg-primary-700 hover:shadow-lg disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : hasExistingProfile ? 'Finish' : 'Save & go to dashboard'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default JobSeekerOnboarding
